import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { loadRazorpayScript } from '../utils/loadRazorpayScript';
import { toast } from 'react-toastify';
import './CheckoutPage.css';

const PLANS = {
    starter: {
        name: 'Starter',
        features: ['1 Clinic', 'Up to 100 Patients', 'Limited Support'],
        monthly: 499,
        yearly: 4990,
    },
    professional: {
        name: 'Professional',
        features: ['Up to 5 Clinics', 'Unlimited Patients', 'WhatsApp & SMS Reminders', 'Email Support'],
        monthly: 999,
        yearly: 9990,
    },
};

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planKey = searchParams.get('plan') || 'professional';
    const plan = PLANS[planKey] || PLANS.professional;

    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
            } else {
                navigate('/login', { state: { from: `/checkout?plan=${planKey}` } });
            }
            setAuthLoading(false);
        });
        return () => unsubscribe();
    }, [navigate, planKey]);

    const currentPrice = plan[billingCycle];
    const savings = billingCycle === 'yearly' ? (plan.monthly * 12) - plan.yearly : 0;

    const handlePayment = async () => {
        if (!user) {
            setError("User not authenticated. Please refresh and try again.");
            return;
        }
        setError('');
        setLoading(true);

        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
            setError('Could not load payment gateway.');
            setLoading(false);
            return;
        }

        try {
            const createOrder = httpsCallable(functions, 'createOrder');
            const { data } = await createOrder({
                amount: currentPrice,
                plan: planKey,
                billingCycle: billingCycle,
            });

            const options = {
                key: data.key,
                amount: data.amount,
                currency: data.currency,
                name: 'ClinicSync',
                description: `${plan.name} Plan - ${billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}`,
                order_id: data.orderId,
                handler: async function (response) {
                    // Verify payment server-side and activate plan
                    try {
                        setLoading(true);
                        const verifyPayment = httpsCallable(functions, 'verifyPayment');
                        const result = await verifyPayment({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        });

                        if (result.data.success) {
                            toast.success('Payment successful! Your plan is now active.');
                            navigate('/dashboard');
                        } else {
                            setError('Payment verification failed. Please contact support.');
                        }
                    } catch (verifyErr) {
                        console.error('Verification error:', verifyErr);
                        setError('Payment verification failed. If money was deducted, please contact support.');
                    } finally {
                        setLoading(false);
                    }
                },
                prefill: {
                    name: user.displayName || '',
                    email: user.email || '',
                },
                theme: {
                    color: '#078158',
                },
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                    },
                },
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (err) {
            console.error('Order creation error:', err);
            setError('Failed to create payment order. Please try again.');
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="checkout-page">
                <div className="checkout-loading">Verifying your session...</div>
            </div>
        );
    }

    return (
        <div className="checkout-page">
            <div className="checkout-header">
                <h1>Checkout</h1>
                <p>Complete your purchase to activate your plan</p>
            </div>

            <div className="checkout-card">
                {/* Plan Summary */}
                <div className="checkout-plan-summary">
                    <h2>{plan.name} Plan</h2>
                    <ul className="checkout-features">
                        {plan.features.map((feature, i) => (
                            <li key={i}>✓ {feature}</li>
                        ))}
                    </ul>
                </div>

                {/* Billing Cycle Toggle */}
                <div className="checkout-section">
                    <h3>Billing Cycle</h3>
                    <div className="billing-cycle-selector">
                        <button
                            className={billingCycle === 'monthly' ? 'active' : ''}
                            onClick={() => setBillingCycle('monthly')}
                        >
                            Monthly
                        </button>
                        <button
                            className={billingCycle === 'yearly' ? 'active' : ''}
                            onClick={() => setBillingCycle('yearly')}
                        >
                            Yearly
                            {savings > 0 && <span className="savings-badge">Save ₹{savings}</span>}
                        </button>
                    </div>
                </div>

                {/* Price */}
                <div className="checkout-price">
                    <span className="price-amount">₹{currentPrice}</span>
                    <span className="price-period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>

                {/* Error */}
                {error && <p className="checkout-error">{error}</p>}

                {/* Pay Button */}
                <button
                    className="checkout-pay-btn"
                    onClick={handlePayment}
                    disabled={loading}
                >
                    {loading ? (
                        <span className="checkout-spinner"></span>
                    ) : (
                        `Pay ₹${currentPrice}`
                    )}
                </button>

                <p className="checkout-secure-text">🔒 Secured by Razorpay</p>

                <button className="checkout-back-btn" onClick={() => navigate('/pricingpage')}>
                    ← Back to Plans
                </button>
            </div>
        </div>
    );
};

export default CheckoutPage;
